/**
 * Imago API client for TMA
 *
 * Routes on the backend:
 *   POST /auth/keys            → create API key (no auth, by tonAddress)
 *   GET  /auth/keys?tonAddress → get balance + keys (no auth, by tonAddress)
 *   GET  /auth/deposit?tonAddress → deposit info (no auth, by tonAddress)
 *   GET  /v1/models            → list models (Bearer)
 *   POST /v1/chat/completions  → chat (Bearer)
 */

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://91.98.224.73:3000'

/** 1 credit = $0.000001 USD (1M credits = $1) */
const CREDITS_PER_USDT = 1_000_000

export type ModelCategory = 'secure' | 'flagship' | 'everyday' | 'coding' | 'research' | 'fast'

export interface Model {
  id: string
  name: string
  description: string
  category: ModelCategory
  status?: 'available' | 'maintenance'
  context_length: number
  pricing: {
    prompt_credits_per_1k: number
    completion_credits_per_1k: number
    prompt_usd_per_token: number
    completion_usd_per_token: number
    margin: string
  }
}

export interface BalanceInfo {
  credits: string       // BigInt as string from backend
  creditsUsd: number    // computed client-side
  keys: Array<{ id: string; name: string; defaultModel: string; createdAt: string }>
}

export interface UsageEntry {
  model: string
  modelName: string
  creditsUsed: string   // BigInt as string
  costUsd: number
  inputTokens: number
  outputTokens: number
  requestCount: number
}

export interface UsageInfo {
  period: '30d'
  totalCreditsUsed: string
  totalCostUsd: number
  usage: UsageEntry[]
}

export interface DepositInfo {
  depositAddress: string
  tonPriceUsd: number
  creditsPerTon: string
  minDeposit: { send: string; netAfterGas: string; credits: string }
  instructions: string
}

/** Vision content part — for image attachments (OpenAI-compatible) */
export type ContentPart =
  | { type: 'text'; text: string }
  | { type: 'image_url'; image_url: { url: string; detail?: 'auto' | 'low' | 'high' } }

export interface ChatMessage {
  role: 'system' | 'user' | 'assistant'
  /** Plain string for text-only messages; ContentPart[] when an image is attached */
  content: string | ContentPart[]
}

class ImagoAPI {
  private apiKey: string = ''

  setKey(key: string) {
    this.apiKey = key
  }

  private bearerHeaders(): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this.apiKey}`,
    }
  }

  /** JSON + Telegram initData header for auth-sensitive endpoints */
  private authHeaders(): Record<string, string> {
    const initData =
      typeof window !== 'undefined'
        ? ((window as any).Telegram?.WebApp?.initData as string | undefined) ?? ''
        : ''
    return {
      'Content-Type': 'application/json',
      'X-Telegram-Init-Data': initData,
    }
  }

  /**
   * Create (or retrieve existing) API key for wallet address.
   * Maps to: POST /auth/keys
   */
  async walletLogin(tonAddress: string): Promise<string> {
    const res = await fetch(`${BASE_URL}/auth/keys`, {
      method: 'POST',
      headers: this.authHeaders(),
      body: JSON.stringify({ tonAddress }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({})) as Record<string, unknown>
      throw new Error((err.error as string | undefined) ?? `Login failed: ${res.status}`)
    }
    const data = await res.json() as { key: string }
    return data.key
  }

  /**
   * Get balance and API keys by wallet address.
   * Maps to: GET /auth/keys?tonAddress=...
   */
  async getBalance(tonAddress: string): Promise<BalanceInfo> {
    const url = `${BASE_URL}/auth/keys?tonAddress=${encodeURIComponent(tonAddress)}`
    const initData =
      typeof window !== 'undefined'
        ? ((window as any).Telegram?.WebApp?.initData as string | undefined) ?? ''
        : ''
    const res = await fetch(url, {
      headers: { 'X-Telegram-Init-Data': initData },
    })
    if (!res.ok) throw new Error(`Failed to get balance: ${res.status}`)
    const data = await res.json() as { credits: string; keys: BalanceInfo['keys'] }
    const creditsUsd = Number(data.credits) / CREDITS_PER_USDT
    return { ...data, creditsUsd }
  }

  /**
   * Get deposit address and current TON rate.
   * Maps to: GET /auth/deposit?tonAddress=...
   */
  async getDepositInfo(tonAddress: string): Promise<DepositInfo> {
    const url = `${BASE_URL}/auth/deposit?tonAddress=${encodeURIComponent(tonAddress)}`
    const res = await fetch(url)
    if (!res.ok) throw new Error(`Failed to get deposit info: ${res.status}`)
    return res.json() as Promise<DepositInfo>
  }

  /**
   * List available AI models with pricing.
   * Public endpoint — no auth required. No custom headers = simple CORS request,
   * no preflight needed (avoids WKWebView preflight issues on iOS).
   */
  async listModels(): Promise<Model[]> {
    const res = await fetch(`${BASE_URL}/v1/models`)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json() as { data: Model[] }
    return data.data
  }

  /**
   * Get per-model usage stats for the last 30 days (from real billing data).
   * Maps to: GET /v1/usage (Bearer)
   */
  async getUsage(): Promise<UsageInfo> {
    const res = await fetch(`${BASE_URL}/v1/usage`, {
      headers: this.bearerHeaders(),
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return res.json() as Promise<UsageInfo>
  }

  /**
   * Revoke an API key by ID.
   * Maps to: DELETE /auth/keys/:keyId?tonAddress=...
   */
  async revokeKey(keyId: string, tonAddress: string): Promise<void> {
    const initData =
      typeof window !== 'undefined'
        ? ((window as any).Telegram?.WebApp?.initData as string | undefined) ?? ''
        : ''
    const res = await fetch(`${BASE_URL}/auth/keys/${keyId}?tonAddress=${encodeURIComponent(tonAddress)}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${this.apiKey}`,
        'X-Telegram-Init-Data': initData,
      },
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({})) as Record<string, unknown>
      throw new Error((err.error as string | undefined) ?? `HTTP ${res.status}`)
    }
  }

  /**
   * Always create a NEW API key (for "New key" button in UI).
   * Maps to: POST /auth/keys/create
   */
  async createKey(
    tonAddress: string,
    name: string,
    defaultModel: string,
  ): Promise<{ key: string; id: string; name: string; defaultModel: string; createdAt: string }> {
    const res = await fetch(`${BASE_URL}/auth/keys/create`, {
      method: 'POST',
      headers: this.authHeaders(),
      body: JSON.stringify({ tonAddress, name, defaultModel }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({})) as Record<string, unknown>
      throw new Error((err.error as string | undefined) ?? `HTTP ${res.status}`)
    }
    return res.json() as Promise<{ key: string; id: string; name: string; defaultModel: string; createdAt: string }>
  }

  /**
   * Generate a short chat title (3-5 words) from the first user message.
   * Non-streaming, max_tokens=20 — cheap and fast.
   */
  async generateTitle(model: string, firstMessage: string): Promise<string> {
    // Cocoon models are too slow/expensive for title generation — use a fast OpenRouter model
    const titleModel = model.startsWith('cocoon/') ? 'meta-llama/llama-3.1-8b-instruct:free' : model
    const prompt = `Write a 3-5 word title for a chat that starts with: "${firstMessage.slice(0, 300).replace(/"/g, "'")}". Reply with ONLY the title, no punctuation, no quotes.`
    const res = await fetch(`${BASE_URL}/v1/chat/completions`, {
      method: 'POST',
      headers: this.bearerHeaders(),
      body: JSON.stringify({
        model: titleModel,
        messages: [{ role: 'user', content: prompt }],
        stream: false,
        max_tokens: 20,
      }),
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json() as { choices?: Array<{ message?: { content?: string } }> }
    let title = data.choices?.[0]?.message?.content?.trim() ?? ''
    // Strip <think> reasoning tags that some models include
    title = title.replace(/<think>[\s\S]*?<\/think>/g, '').trim()
    title = title.replace(/^["']|["']$/g, '')
    return title.slice(0, 60) || firstMessage.slice(0, 40).trim()
  }

  /**
   * Stream chat completions — yields text chunks via SSE.
   * Maps to: POST /v1/chat/completions { stream: true } (Bearer)
   * Pass an AbortSignal to support stop-generation.
   */
  async *streamChat(
    model: string,
    messages: ChatMessage[],
    signal?: AbortSignal,
  ): AsyncGenerator<string, void, unknown> {
    const res = await fetch(`${BASE_URL}/v1/chat/completions`, {
      method: 'POST',
      headers: this.bearerHeaders(),
      body: JSON.stringify({ model, messages, stream: true }),
      signal,
    })

    if (!res.ok) {
      const err = await res.json().catch(() => ({})) as Record<string, unknown>
      throw new Error((err.error as string | undefined) ?? `HTTP ${res.status}`)
    }

    if (!res.body) throw new Error('No response body')

    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    try {
      while (true) {
        if (signal?.aborted) break
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() ?? ''

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          const data = line.slice(6).trim()
          if (data === '[DONE]') return
          try {
            const json = JSON.parse(data) as {
              choices?: Array<{ delta?: { content?: string } }>
            }
            const content = json.choices?.[0]?.delta?.content
            if (content) yield content
          } catch {
            // skip malformed SSE chunks
          }
        }
      }
    } finally {
      reader.releaseLock()
    }
  }
}

export const api = new ImagoAPI()
